import { FeatureFlag, Size } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { generatePath } from '@ps/web-core-utils';
import { useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import OnboardingContainer from '@swf/components/Onboarding/OnboardingContainer/OnboardingContainer';
import Redirect from '@swf/components/Redirect/Redirect';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import {
  DownloadBanner,
  ModalPath,
  PriorityImage,
  checkSharedLiquidity,
  useSAGGameUrl,
  useSAGHandleRegisterOpened,
  useSaveLastUsedPath
} from '@swf/poker';

import type { SpinAndGo as SpinAndGoType } from '../../../../../../../libs/core/store/src/lib/types/Poker';
import { usePokerSpinAndGoWidget } from '../../model';
import { AssetType, LocalSettingsPath, ModalRoute } from '../../types';
import type { Game, GameTypeName } from '../../types/Game';
import { GAME_TYPE_URL_MAP, GAME_URL_TYPE_MAP, GameType } from '../../types/Game';
import { findSmallestBuyInId, mapGameData, mapTilesToDisplay } from '../../utils';
import { getOnboardingSteps } from '../../utils/onboarding';
import { sortPMGames, sortRMGames } from '../../utils/sortGames';
import Maintenance from '../Maintenance/Maintenance';
import Navigation from '../Navigation/Navigation';
import OnboardStep from '../OnboardFlow/OnboardStep';
import PlayNowButton from '../Sidebar/PlayNowButton';
import Sidebar from '../Sidebar/Sidebar';
import TilesContainer from '../Tiles/TilesContainer';

import { TXTCLI_If_you_enjoy_Spin_X_GoX_downlo_ELL } from './SpinAndGo.messages.json';
import styles from './SpinAndGo.scss';

const SpinAndGo: React.FC = () => {
  const aliases = useSelector(select.cms.getAliases);
  const { widgetSelect, widgetDispatch, widgetId } = usePokerSpinAndGoWidget();
  const { tab: activeTab } = useSelector(select.cms.getUrlParams);
  const games = useSelector(widgetSelect.games);
  const hasError = useSelector(widgetSelect.hasError);
  const { greaterThan } = useScreenSize();
  const smallScreen = !greaterThan(Size.Small);
  const isMobile = useSelector(select.device.isMobile);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const { poker } = useSelector(select.localSettings.get);
  const { modal, localSettings } = useDispatch<Dispatch>();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const maxHoldemEnabled = useFeature(FeatureFlag.PKRSpinAndGoMaxHoldemAvailable);
  const omahaEnabled = useFeature(FeatureFlag.PKRSpinAndGoOmahaAvailable);
  const holdemEnabled = useFeature(FeatureFlag.PKRSpinAndGoHoldemAvailable);
  const flashEnabled = useFeature(FeatureFlag.PKRSpinAndGoFlashAvailable);
  const holdemSixEnabled = useFeature(FeatureFlag.PKRSpinAndGoHoldemSixAvailable);
  const site = useSelector(select.cms.getSiteId);
  const isSharedLiquidityEnabled = useSelector(widgetSelect.isSharedLiquidityAvailable);
  const showPlayNowButton = (isMobile || smallScreen) && !isEmbedded;

  const gameModes = React.useMemo(
    () =>
      mapGameData({
        isHoldemEnabled: holdemEnabled,
        isFlashEnabled: flashEnabled,
        isMaxEnabled: maxHoldemEnabled,
        isOmahaEnabled: omahaEnabled,
        isHoldemSixEnabled: holdemSixEnabled
      }),
    [maxHoldemEnabled, omahaEnabled, holdemEnabled, flashEnabled, holdemSixEnabled]
  );
  const setUrlPath = useSaveLastUsedPath();

  const [selectedPage, setSelectedPage] = useState<GameTypeName>(
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    GAME_URL_TYPE_MAP[activeTab] || gameModes[0]?.tabName || GameType.Holdem
  );
  const [selectedGame, setSelectedGame] = useState<Game>();

  const onboardingModalShown = !!poker?.spinAndGo?.onboardingModalShown;
  const onboardingModalFinished = !!poker?.spinAndGo?.onboardingFinished;

  const selectedGameEntry = `${selectedPage}-${isPlayMoney ? 'PM' : 'RM'}`;
  const tableUrl = useSAGGameUrl({
    isFlash: selectedGame?.isFlash && selectedPage === GameType.HoldemFlash,
    isMax: selectedGame?.isMax,
    scriptId: selectedGame?.scriptId || 0
  });
  const sortGames = isPlayMoney ? sortPMGames : sortRMGames;
  const tileScriptId = selectedGame?.scriptId || 0;
  const backgroundAssetUrl = useSelector((state: RootState) =>
    widgetSelect.getActiveAssetConfig(state, selectedPage, AssetType.Background)
  );
  const tilesToDisplay = React.useMemo(
    () =>
      mapTilesToDisplay(games, selectedPage)
        .sort(sortGames)
        .map((tile) => ({
          ...tile,
          isEnabled: checkSharedLiquidity(isSharedLiquidityEnabled, tile.tournSites, site)
        })),
    [games, selectedPage, sortGames, isSharedLiquidityEnabled, site]
  );

  useEffect(() => {
    if (!tilesToDisplay.length) return;

    let selectedGameId = poker?.spinAndGo?.[selectedGameEntry as keyof SpinAndGoType];
    let selectedGame;
    if (selectedGameId) {
      selectedGame = tilesToDisplay.find(
        (tile) => tile.scriptId === selectedGameId && tile.isEnabled
      );
    }

    if (!selectedGameId || !selectedGame) {
      selectedGameId = findSmallestBuyInId(tilesToDisplay.filter((tile) => tile.isEnabled));
      selectedGame = tilesToDisplay.find((tile) => tile.scriptId === selectedGameId);
    }

    setSelectedGame(selectedGame);
  }, [tilesToDisplay, poker, selectedPage, selectedGameEntry, site]);

  const handlePageSelected = (page: GameTypeName) => {
    setSelectedPage(page);
    widgetDispatch.setRandomRBWTicker();
    setUrlPath(GAME_TYPE_URL_MAP[page]);
  };

  const handleHowPlayClicked = () =>
    modal.enqueue({
      path: ModalRoute.HowItWorks,
      locationState: { gameType: selectedPage, widgetId, isSharedLiquidityEnabled }
    });

  const handleRegisterOpened = useSAGHandleRegisterOpened({
    tableUrl,
    shouldCloseOldModalFirst: false,
    tileScriptId,
    isFlash: selectedPage === GameType.HoldemFlash
  });

  const handleGameSelected = useCallback(
    (game: Game) => {
      if (game.isEnabled) {
        localSettings.set({ poker: { spinAndGo: { [selectedGameEntry]: game.scriptId } } });
        setSelectedGame(game);
      } else {
        modal.enqueue({ path: ModalPath.VerifyAccount });
      }
    },
    [modal, localSettings, selectedGameEntry]
  );

  useEffect(() => {
    if (!onboardingModalShown) {
      modal.enqueue({ path: ModalRoute.OnboardingTourStart });
    }
  }, [modal, onboardingModalShown, isMobile]);

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (onboardingModalShown && !onboardingModalFinished) {
        localSettings.set({ poker: { spinAndGo: { onboardingFinished: true } } });

        e.preventDefault();
        e.returnValue = ''; // Needed for Chrome;
      }
    },
    [onboardingModalShown, onboardingModalFinished, localSettings]
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  // This is used to select the active tab if the user is redirected
  useEffect(() => {
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    setSelectedPage(GAME_URL_TYPE_MAP[activeTab] || gameModes[0]?.tabName || GameType.Holdem);
  }, [activeTab, gameModes]);

  // If the user enters a url for game type that is not available, we should redirect to the first available tab
  if (
    activeTab &&
    aliases.pokerSpinAndGo &&
    // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
    !gameModes.some((game) => game.tabName === GAME_URL_TYPE_MAP[activeTab])
  ) {
    return <Redirect url={generatePath(aliases.pokerSpinAndGo, {})} />;
  }

  return (
    <OnboardingContainer
      OnboardStep={<OnboardStep />}
      steps={getOnboardingSteps(isMobile)}
      onboardingStoragePath={LocalSettingsPath.SpinAndGo}
      hideOnboarding={false}
    >
      <div
        className={classNames(styles.wrapper, {
          [styles.onboardingActive]: onboardingModalShown && !onboardingModalFinished,
          [styles[`lobbyBgr${selectedPage}`]]: !backgroundAssetUrl
        })}
      >
        <div className={styles.container}>
          <div className={styles.content}>
            <Navigation
              navTabs={gameModes}
              selectedTab={selectedPage}
              onSelectTab={handlePageSelected}
            />
            <div className={isDesktopEmbedded ? styles.embeddedContainer : styles.mainContainer}>
              {hasError ? (
                <Maintenance />
              ) : (
                <TilesContainer
                  tiles={tilesToDisplay}
                  gameType={selectedPage}
                  selectedId={tileScriptId}
                  onGameSelect={handleGameSelected}
                />
              )}
              <Sidebar
                gameType={selectedPage}
                onHowToPlayClick={handleHowPlayClicked}
                onRegisterOpen={handleRegisterOpened}
                hasSelectedGame={!!selectedGame}
                showPlayButton={(!isMobile && !smallScreen) || isEmbedded}
              />
            </div>
          </div>
        </div>
        {backgroundAssetUrl ? (
          <PriorityImage
            imageUrl={backgroundAssetUrl}
            fetchPriority="high"
            altText={`${selectedPage} background image`}
            className={styles.configuredAsset}
          />
        ) : (
          <div
            className={classNames(styles.animationContainer, {
              [styles.onboardingActive]: onboardingModalShown && !onboardingModalFinished
            })}
          >
            <div
              className={classNames(styles.lobbyBgr, styles[`lobbyBgr${selectedPage}`])}
              key={selectedPage}
            />
          </div>
        )}
        {showPlayNowButton && (
          <PlayNowButton onClick={handleRegisterOpened} hasSelectedGame={!!selectedGame} />
        )}
        {!isEmbedded && (
          <DownloadBanner isOnboardingFinished label={TXTCLI_If_you_enjoy_Spin_X_GoX_downlo_ELL} />
        )}
      </div>
    </OnboardingContainer>
  );
};

export default SpinAndGo;
